import React, { useState } from "react";
import {
  Divider,
  Grid,
  Skeleton,
  Typography,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Pagination,
} from "@mui/material";
import OngsClient from "../api/OngsClient";
import NGOCard from "~/components/NGOs/Card";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import { useParams, Navigate } from 'react-router-dom';
import SearchField from '~/components/SearchField';
import NoItemsMessage from "~/components/NoItemsMessage";
import { useNormalizedSearch } from '~/single-page/hooks/useNormalizedSearch';
import { APP_ROUTES } from "~/single-page/constants/routes";

export default function CauseNgosPage() {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  if (!id) {
    return <Navigate to={APP_ROUTES.CAUSES} replace />;
  }

  const {
    data: ongs = [],
    success,
    pending,
  } = useApiCall({
    apiCall: () => OngsClient.cause_ngos(id),
    init: true,
    deps: [id],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [normalizedSearchTerm, setNormalizedSearchTerm] = useState('');

  const currentCause = ongs[0]?.causes?.find(cause => cause.id === parseInt(id));

  const filteredOngs = useNormalizedSearch(ongs, normalizedSearchTerm, [
    'name',
    'description'
  ]);

  const renderSkeletons = () => (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        {Array.from(new Array(6)).map((_, index) => (
          <Grid item xs={12} sm={6} lg={4} key={index}>
            <Skeleton 
              variant="rectangular" 
              height={216} 
              sx={{ borderRadius: 1 }}
              animation="wave"
            />
          </Grid>
        ))}
      </Grid>
    </Container>
  );

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(filteredOngs.length / itemsPerPage);
  const currentOngs = filteredOngs.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (pending) return renderSkeletons();

  if (!success || !ongs.length) {
    return <NoItemsMessage itemName="ONG" gender="feminino" />;
  }

  if (!currentCause) {
    return <Navigate to={APP_ROUTES.CAUSES} replace />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box sx={{ 
        maxWidth: '100%',
        px: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          sx={{ 
            mb: 2, 
            fontWeight: 'bold',
            textAlign: 'center'
          }}
        >
          ONGs e Projetos da causa {currentCause.name}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          {currentCause.description}
        </Typography>

        <SearchField 
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setNormalizedSearchTerm(e.target.normalizedValue);
            setPage(1); // Reset para primeira página ao pesquisar
          }}
          label="Pesquisar ONGs..."
        />
        
        <Divider sx={{ width: "100%", mb: 4 }} />
        
        <Grid container spacing={3}>
          {currentOngs.map((ong) => (
            <Grid item xs={12} sm={6} key={ong.id}>
              <NGOCard ong={ong} />
            </Grid>
          ))}
          {filteredOngs.length === 0 && searchTerm && (
            <Box sx={{ 
              width: '100%', 
              textAlign: 'center', 
              py: 4 
            }}>
              <Typography color="text.secondary">
                Nenhuma ONG encontrada com o termo "{searchTerm}"
              </Typography>
            </Box>
          )}
        </Grid>

        {filteredOngs.length > itemsPerPage && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mt: 4
          }}>
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Container>
  );
}
